import { Alert, Button, Select } from "antd";
import qs from "query-string";
import { useState } from "react";
import { useHistory } from "react-router";
import { getBackendUrl, STORES } from "../utils";

type Props = {
  platform: STORES;
};

/*
This component allows people to choose which environment in an accounting system to connect to Rutter.
This is used when a user has multiple environments (e.g., multiple Dynamics 365 environments).
After selecting an environment, the user will be redirected to company selection with companies filtered to that environment.

The query parameters contain:
- environmentNames: array of environment names for the user to select
- credentialId: the credential ID to pass back to the backend
- state: state parameter to preserve through the auth flow
*/
function EnvironmentSelect(props: Props) {
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { platform } = props;

  const history = useHistory();
  const { search } = history.location;

  const parsedSearch: any = qs.parse(search, { arrayFormat: "comma" });

  const { state, credentialId, errorMessage } = parsedSearch;
  const environmentNames = parseArray(parsedSearch.environmentNames);

  if (environmentNames == null || environmentNames.length === 0) {
    history.replace("/error");
    return null;
  }

  const handleComplete = () => {
    if (!selectedEnvironment) {
      return;
    }

    setIsLoading(true);
    const q = qs.stringify({
      state,
      credentialId,
      environmentName: selectedEnvironment,
    });
    let url;
    switch (platform) {
      case STORES.DYNAMICS365:
        url = `${getBackendUrl()}/dynamics/finish-environment-select?${q}`;
        break;
      default:
        history.replace("/error");
        return null;
    }
    window.location.href = url;
    setIsLoading(false);
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 99,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ width: 800 }}>
        {errorMessage && (
          <Alert
            message="Error"
            description={
              <div style={{ whiteSpace: "pre-line" }}>{errorMessage}</div>
            }
            type="error"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <div className="font-semibold">
          Select the Environment you would like to connect:
        </div>

        <Select
          showSearch
          style={{ width: "100%", marginTop: 20, marginBottom: 20 }}
          placeholder="Select Environment"
          onSelect={(val: any) => setSelectedEnvironment(val as string)}
          defaultValue={undefined}
        >
          {environmentNames.map((name) => {
            return (
              <Select.Option key={name} value={name}>
                {name}
              </Select.Option>
            );
          })}
        </Select>
        <Button
          type="primary"
          block
          disabled={isLoading || !selectedEnvironment}
          onClick={handleComplete}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

function parseArray(param: any): string[] {
  if (typeof param === "string") {
    return param.split(",").map((val) => decodeURIComponent(val));
  }
  if (Array.isArray(param)) {
    return param.map((val) =>
      typeof val === "string" ? decodeURIComponent(val) : val
    );
  }
  return [];
}

export default EnvironmentSelect;
